import { HttpErrorResponse } from "@angular/common/http";
import { Component, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { AuthenticationService } from '@core/services';

@Component({
  templateUrl: './confirmEmail.component.html',
  styleUrls: ['./confirmEmail.component.css']
})
export class ConfirmEmailComponent implements OnInit {

  public isLoading = false;
  public showSuccess = false;
  public showError = false;
  public errorMessage = '';

  constructor(private _authService: AuthenticationService, private _route: ActivatedRoute) { }

  ngOnInit(): void {
    this.confirmEmail();
  }

  get titleKey(): string {
    if (this.isLoading) {
      return 'general.confirm_email_loading_title';
    }

    if (this.showSuccess) {
      return 'general.confirm_email_success_title';
    }

    return 'general.confirm_email_error_title';
  }

  get descriptionKey(): string {
    if (this.isLoading) {
      return 'general.confirm_email_loading_description';
    }

    if (this.showSuccess) {
      return 'general.confirm_email_success_description';
    }

    return 'general.confirm_email_error_description';
  }

  private confirmEmail = () => {
    this.isLoading = true;
    this.showError = this.showSuccess = false;
    this.errorMessage = '';

    const token = this._route.snapshot.queryParams['token']?.trim();
    const email = this._route.snapshot.queryParams['email']?.trim();

    if (!token || !email) {
      this.isLoading = false;
      this.showError = true;
      this.errorMessage = 'errors.confirm_email_invalid_link';
      return;
    }

    this._authService.confirmEmail(email, token)
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.showSuccess = true;
        },
        error: (error: unknown) => {
          this.isLoading = false;
          this.showError = true;
          this.errorMessage = this.resolveErrorMessage(error);
        }
      });
  }

  private resolveErrorMessage(error: unknown): string {
    const text = this.extractErrorText(error);

    if (!text) {
      return 'errors.confirm_email_unknown';
    }

    if (text.includes('already') && text.includes('confirm')) {
      return 'errors.confirm_email_already_confirmed';
    }

    if (text.includes('expired')) {
      return 'errors.confirm_email_expired';
    }

    if (
      text.includes('invalid token') ||
      text.includes('token invalid') ||
      text.includes('invalid confirmation')
    ) {
      return 'errors.confirm_email_invalid_link';
    }

    if (text.includes('user') && text.includes('not found')) {
      return 'errors.confirm_email_user_not_found';
    }

    return 'errors.confirm_email_unknown';
  }

  private extractErrorText(error: unknown): string {
    if (error == null) {
      return '';
    }

    if (error instanceof HttpErrorResponse) {
      const responseMessages = this.flattenErrorMessages(error.error).join(' ');
      return `${responseMessages} ${error.message || ''}`.trim().toLowerCase();
    }

    return this.flattenErrorMessages(error).join(' ').trim().toLowerCase();
  }

  private flattenErrorMessages(value: unknown): string[] {
    if (value == null) {
      return [];
    }

    if (typeof value === 'string') {
      return [value];
    }

    if (Array.isArray(value)) {
      return value.flatMap(item => this.flattenErrorMessages(item));
    }

    if (typeof value !== 'object') {
      return [String(value)];
    }

    const source = value as Record<string, unknown>;
    const direct = ['message', 'error', 'title', 'detail', 'description']
      .flatMap(key => this.flattenErrorMessages(source[key]));

    if (source.errors) {
      direct.push(...this.flattenErrorMessages(source.errors));
    }

    if (direct.length > 0) {
      return direct;
    }

    return Object.values(source).flatMap(item => this.flattenErrorMessages(item));
  }

}
